import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  Fragment
} from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Formik, FormikProps } from 'formik'
import { Icon } from '@iconify/react'

import {
  fetchDetails,
  submitBulkFeedback,
  updateAssignmentStatus
} from '@pepper/utils/src/api/assignments/assignments'
import type { AssignmentFeedback } from '@pepper/utils/src/api/assignments/assignments'
import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { getUserId } from '@pepper/utils/src/api/functions/authHelpers'

import { TextAreaField } from '../FormFields/FormFields'
import { Modal, Spin, Tooltip } from '../../atoms'
import { useShowMessage } from '../../providers'
import RateField from '../RateField/RateField'
import styles from './AcceptSubmissionModal.module.css'

type Props = {
  assignmentIds?: string[]
  shouldSubmitAssignmentApproval?: boolean
  companyId: string | null
  isOps: boolean
  refetch?: () => void
  defaultRating?: number
}

export const AcceptSubmissionModal = NiceModal.create<Props>(
  ({
    assignmentIds = [],
    shouldSubmitAssignmentApproval = false,
    isOps = false,
    companyId,
    refetch,
    defaultRating
  }) => {
    const [assignmentsState, setAssignmentsState] = useState<Assignment[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const modal = useModal()
    const showMessage = useShowMessage()
    const userId = getUserId()

    const groupedAssignmentsByCreator = useMemo(() => {
      const groupedAssignments: Record<string, Assignment[]> = {}
      assignmentsState.forEach(assignment => {
        const creatorId = assignment?.writer_allotted?.id || ''
        if (groupedAssignments[creatorId]) {
          groupedAssignments[creatorId].push(assignment)
        } else {
          groupedAssignments[creatorId] = [assignment]
        }
      })

      return groupedAssignments
    }, [assignmentsState])

    const submitFeedback = useCallback(
      async (values, submissionAssignmentIds) => {
        const feedback: AssignmentFeedback[] = assignmentsState
          .filter(assignment => submissionAssignmentIds.includes(assignment.id))
          .map(assignment => {
            return {
              assignment_id: assignment.id,
              feedback_text: values['feedback_text'] || '',
              rating: values['rating']
            }
          })

        try {
          setIsSubmitting(true)
          const { status, error } = await submitBulkFeedback(feedback)
          !shouldSubmitAssignmentApproval &&
            showMessage({
              type: 'success',
              message: 'Feedback submitted  successfully'
            })
          if (status === 'error') throw new Error(error)
        } catch (error) {
          showMessage({ type: 'error', message: error.message })
        } finally {
          setIsSubmitting(false)
        }
      },
      [assignmentsState, shouldSubmitAssignmentApproval, showMessage]
    )

    const approveAssignmentsAndSubmitFeedback = useCallback(
      async (values, isFeedbackProvided) => {
        try {
          setIsSubmitting(true)
          const results = await Promise.allSettled(
            assignmentsState.map(assignment => {
              return updateAssignmentStatus({
                id: assignment.id,
                body: {
                  assigner_role: isOps ? 'client_ops' : 'client',
                  assigner_id: userId || '',
                  assignee_id: (isOps ? assignment?.client?.id : userId) || '',
                  assignment_id: assignment.id,
                  status: ASSIGNMENT_STATUS.CLI_FIN.id
                }
              })
            })
          )
          const successfullyApprovedAssignmentIds = results
            .filter(
              (result: PromiseSettledResult<ApiResponse>) =>
                result.status === 'fulfilled' && result.value.status === 'ok'
            )
            .map(
              (result: PromiseSettledResult<ApiResponse>) =>
                (result as PromiseFulfilledResult<ApiResponse>).value?.data?.id
            )
          if (successfullyApprovedAssignmentIds.length > 0) {
            showMessage({
              type: 'success',
              message: 'Assignments approved successfully'
            })
          }
          if (
            assignmentsState.length > successfullyApprovedAssignmentIds.length
          ) {
            showMessage({
              type: 'error',
              message: `Failed to approve ${
                assignmentsState.length -
                successfullyApprovedAssignmentIds.length
              } assignmentsState. Please try again.`
            })
          }
          if (isOps) {
            setIsSubmitting(false)
            return
          }
          // dirty mean feedback form has values, and no need to submit feedback if no assignment was approved
          if (
            isFeedbackProvided &&
            successfullyApprovedAssignmentIds.length > 0
          ) {
            await submitFeedback(values, successfullyApprovedAssignmentIds)
          }
        } catch (error) {
          showMessage({ type: 'error', message: error.message })
        } finally {
          setIsSubmitting(false)
        }
      },
      [assignmentsState, userId, showMessage, submitFeedback, isOps]
    )

    const assignmentFeedbackUI = useCallback(
      (props: FormikProps<Record<string, number | undefined>>) => {
        if (assignmentsState.length === 0) return null

        const hiddenItems =
          assignmentsState.length < 3 ? null : (
            <div className={styles.hiddenAssignments}>
              {assignmentsState.slice(3).map(assignment => (
                <div className={styles.hoverItem} key={assignment.id}>
                  {assignment.assignment_title}
                </div>
              ))}
            </div>
          )

        return (
          <Fragment>
            {isOps && (
              <div className={styles.opsHeader}>
                Are you sure you have confirmation to approve the assignment(s)?
                Once approved, they won’t be marked for rework.
              </div>
            )}
            <div className={styles.feedbackModalSingleContent}>
              <div className={styles.assignmentTitleContainer}>
                {assignmentsState.slice(0, 3).map(assignment => (
                  <div key={assignment.id} className={styles.assignmentTitle}>
                    <Icon icon="lucide:file-text" className={styles.fileIcon} />
                    <div className={styles.title}>
                      {assignment.assignment_title}
                    </div>
                  </div>
                ))}
                {assignmentsState.length > 3 && (
                  <Tooltip title={hiddenItems} placement="bottom">
                    <div className={styles.extra}>
                      + {assignmentsState.length - 3} more
                    </div>
                  </Tooltip>
                )}
              </div>
              {!isOps && (
                <Fragment>
                  <div className={styles.inputHeader}>
                    How was your overall experience?
                  </div>
                  <RateField />
                </Fragment>
              )}
              {!isOps && (
                <Fragment>
                  <div className={styles.inputHeader}>
                    Tell us more
                    <span className={styles.optionText}>(optional)</span>
                  </div>
                  <div className={styles.feedbackCommentContainer}>
                    <TextAreaField
                      name="feedback_text"
                      required={false}
                      fieldProps={{
                        placeholder:
                          'Is there anything specific you loved or think could be improved?',
                        className: styles.feedbackCommentsTextboxLarge,
                        rows: 4
                      }}
                      setFieldTouched={props.setFieldTouched}
                    />
                  </div>
                </Fragment>
              )}
            </div>
          </Fragment>
        )
      },
      [assignmentsState, isOps]
    )

    const modalTitle = useMemo(() => {
      if (shouldSubmitAssignmentApproval) {
        return (
          <span className={styles.modalTitle}>
            Approve assignment ({assignmentsState?.length})
          </span>
        )
      }
      return <span className={styles.modalTitle}>Help us improve!</span>
    }, [shouldSubmitAssignmentApproval, assignmentsState?.length])

    const modalOkButtonText = useMemo(() => {
      if (shouldSubmitAssignmentApproval && assignmentsState.length === 1) {
        return 'Approve assignment'
      } else {
        return `Approve assignments (${assignmentsState?.length})`
      }
    }, [shouldSubmitAssignmentApproval, assignmentsState])

    const initialValues = useMemo(() => {
      const _initialValues: Record<string, number | undefined> = {}
      Object.keys(groupedAssignmentsByCreator).forEach(creatorId => {
        _initialValues['rating'] = defaultRating
      })
      _initialValues.rating = defaultRating
      return _initialValues
    }, [groupedAssignmentsByCreator, defaultRating])

    const getISubmitButtonDisabled = useCallback(
      props => {
        if (props.values?.rating) return false
        if (isOps) return false
        if (props.isSubmitting) return true
        if (isSubmitting) return true
        return true
      },
      [isSubmitting, isOps]
    )

    const fetchAssignmentDetails = useCallback(async () => {
      if (!companyId) return
      try {
        const details = await Promise.all(
          assignmentIds.map(async id => fetchDetails({ id, companyId }))
        )
        const response = details.map(detail => {
          if (detail.data) {
            return detail.data
          } else {
            throw new Error('Something went wrong')
          }
        })
        if (response.length > 0) {
          setAssignmentsState(response)
        }
      } catch (error) {
        showMessage({ type: 'error', message: 'Something went wrong' })
      }
    }, [assignmentIds, companyId, showMessage])

    useEffect(() => {
      fetchAssignmentDetails()
    }, [fetchAssignmentDetails])

    const renderInsideModal = (
      props: FormikProps<Record<string, number | undefined>>
    ) => {
      if (assignmentsState.length === 0 || isSubmitting) {
        return (
          <div className={styles.spinner}>
            <Spin />
          </div>
        )
      } else {
        return assignmentFeedbackUI(props)
      }
    }

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={() => {
          refetch?.()
        }}
      >
        {(props): React.ReactNode => {
          return (
            <Modal
              visible={modal.visible}
              maskClosable={false}
              className={styles.feedbackModal}
              onOk={async () => {
                if (shouldSubmitAssignmentApproval) {
                  await approveAssignmentsAndSubmitFeedback(
                    props.values,
                    // either we got a default value for rating or user provided feedback
                    // so we need to submit feedback as well
                    defaultRating !== undefined || props.dirty
                  )
                } else {
                  await submitFeedback(
                    props.values,
                    assignmentsState.map(assignment => assignment.id)
                  )
                }
                props.submitForm()
                modal.remove()
              }}
              okButtonProps={{
                // when approval is not needed, disable submit button until user provide value or there is default rating provided
                disabled: getISubmitButtonDisabled(props) || isSubmitting
              }}
              cancelButtonProps={{
                className: styles.cancelBtn,
                disabled: isSubmitting
              }}
              onCancel={() => {
                modal.remove()
              }}
              title={modalTitle}
              okText={modalOkButtonText}
              bodyStyle={{ padding: '2rem' }}
              closeIcon={
                <div className={styles.closeIconContainer}>
                  <Icon icon="lucide:x" className={styles.closeIcon} />
                </div>
              }
              closable={isSubmitting ? false : true}
            >
              {renderInsideModal(props)}
            </Modal>
          )
        }}
      </Formik>
    )
  }
)
